import { SfxToggle, useSfx, useTts } from '@slingu/ui';
import { useEffect, useRef, useState } from 'react';

// A voz default vem do servidor (Cloudflare Function env ELEVENLABS_VOICE_ID
// ou fallback em código). Passamos `undefined` no useTts.play pra usar
// a default — assim mudanças de voz não exigem deploy do front.

import { commitScore, getHighScore } from './highscores.js';
import { drawBackground, drawSlingu, drawGround, sizeCanvas } from './slingu-draw.js';
import { LEGACY_POOL, type FlappyPair } from './vocabulary.js';

const HIGH_SCORE_ID = 'flappy';

export type { FlappyPair } from './vocabulary.js';

export type FlappySlinguProps = {
  onExit: (result: { score: number; caught: number; missed: number; xp: number }) => void;
};

type Phase = 'menu' | 'playing' | 'over';

// --- Mobile-first portrait world ----------------------------------------
const WORLD_W = 480;
const WORLD_H = 760;
const GROUND_Y = WORLD_H - 70;
const GROUND_H = WORLD_H - GROUND_Y;
const PLAYER_X = 130;
const PLAYER_R = 24;
const GRAVITY = 1400;
const FLAP_V = -400;
const SCROLL_SPEED = 160;
const CARD_W = 170;
const CARD_H = 64;
const CARD_PAIR_GAP = 170; // vertical distance between the two cards in a challenge
// Intervalo entre desafios. Mais tempo = mais chance de ler o prompt PT e
// ouvir o TTS antes dos cards chegarem ao jogador. 5.0s é o meio-termo:
// 4.8 (original) ficava apertado, 6.0 ficou longo demais. 5.0 dá ~2s de
// tela vazia entre cards, sem encostar nem arrastar.
const CHALLENGE_INTERVAL = 5.0;
// Delay mínimo aplicado ao spawnTimer logo após o jogador resolver um
// desafio (acerto, erro por toque ou erro por ignorar). Garante que o
// próximo par nunca spawna "colado" no anterior — mesmo se o jogador
// tocar num card bem tarde, o próximo só entra depois desse buffer.
const POST_CHALLENGE_MIN_DELAY = 1.5;
// Se ainda houver card ativo além desta linha X, adia o spawn do próximo
// desafio em vez de criar sobreposição visual. WORLD_W * 0.5 = metade
// direita da tela.
const SPAWN_GUARD_X = WORLD_W * 0.5;
const HIT_GRACE = 0.7;
const XP_CORRECT = 15;
const HEART_R = 18;
// Cooldown entre tentativas de spawn de coração (pickup). Fica "pouco" de
// propósito — em média aparece um a cada ~13s, e só se o player não estiver
// com vida cheia. Também começa com folga (12s após o início da partida).
const HEART_COOLDOWN_MIN = 12;
const HEART_COOLDOWN_MAX = 18;
const MAX_HEARTS = 3;

type CardState = 'neutral' | 'correct' | 'wrong';

type HeartPickup = {
  id: string;
  x: number;
  y: number;
  consumed: boolean;
  /** bob phase for a light float animation */
  phase: number;
};

type Card = {
  id: string;
  challengeId: string;
  isCorrect: boolean;
  label: string;
  x: number;
  y: number;
  consumed: boolean;
  state: CardState;
};

// Preserves the difficulty curve while randomizing order: shuffle within
// blocks so early blocks stay easy and later blocks stay hard across runs.
function blockShuffle<T>(arr: T[], blockSize = 10): T[] {
  const out: T[] = [];
  for (let i = 0; i < arr.length; i += blockSize) {
    const block = arr.slice(i, i + blockSize);
    for (let j = block.length - 1; j > 0; j--) {
      const k = Math.floor(Math.random() * (j + 1));
      const tmp = block[j] as T;
      block[j] = block[k] as T;
      block[k] = tmp;
    }
    out.push(...block);
  }
  return out;
}

/**
 * Flappy Slingu — mobile-first, portrait. Cada desafio mostra duas palavras
 * em inglês flutuando na mesma coluna (uma acima, outra abaixo). O jogador
 * voa pra tocar na palavra CERTA (tradução do prompt em PT no topo).
 *
 * Sem canos / sem parede: é só escolher a altura certa. Palavra errada
 * custa 1 coração. Se não tocar em nenhuma, passa em branco (sem perda).
 * Chão = -1 coração + quica.
 */
export function FlappySlingu({ onExit }: FlappySlinguProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);

  const [phase, setPhase] = useState<Phase>('menu');
  const [hud, setHud] = useState({ score: 0, caught: 0, missed: 0, hearts: 3 });
  const [highScore, setHighScore] = useState<number>(() => getHighScore(HIGH_SCORE_ID));
  const [newRecord, setNewRecord] = useState<boolean>(false);
  const promptRef = useRef<string>('');

  const sfx = useSfx();
  const sfxRef = useRef(sfx);
  sfxRef.current = sfx;
  const tts = useTts();
  const ttsRef = useRef(tts);
  ttsRef.current = tts;
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;
  const pairsRef = useRef<FlappyPair[]>(LEGACY_POOL);
  pairsRef.current = LEGACY_POOL;

  const gameRef = useRef({
    scrollX: 0,
    playerY: WORLD_H / 2,
    playerVy: 0,
    cards: [] as Card[],
    hearts3d: [] as HeartPickup[],
    queue: [] as FlappyPair[],
    spawnTimer: 1.2,
    heartTimer: HEART_COOLDOWN_MIN,
    heartCounter: 0,
    wingPhase: 0,
    blinkT: 2 + Math.random() * 2,
    blink: 0,
    pops: [] as { x: number; y: number; t: number; color: string; text: string }[],
    score: 0,
    caught: 0,
    missed: 0,
    hearts: 3,
    alive: true,
    hitCooldown: 0,
    firstInput: false,
    challengeCounter: 0,
    lastCorrectSides: [] as boolean[],
    // (voiceToggle removido — usamos uma voz fixa do servidor agora)
  });

  const startedRef = useRef(false);

  useEffect(() => {
    if (phase !== 'playing') return;
    if (startedRef.current) return;
    startedRef.current = true;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const g = gameRef.current;
    // Reset
    g.scrollX = 0;
    g.playerY = WORLD_H / 2;
    g.playerVy = 0;
    g.cards = [];
    g.hearts3d = [];
    // Block-shuffle keeps the easy→hard curve (shuffle within each 10-word
    // block) while giving every run a fresh word order.
    // blockShuffle preserva a curva de dificuldade (blocos no meio do pool já
    // são A2→B1; blocos finais são B2) mas mistura os tópicos dentro do bloco.
    g.queue = blockShuffle(pairsRef.current, 50);
    // Primeiro desafio tem mais folga (2.3s) pra o jogador se orientar —
    // bater asa, ouvir o primeiro TTS, ler o prompt PT no topo.
    g.spawnTimer = 2.3;
    g.heartTimer = HEART_COOLDOWN_MIN;
    g.heartCounter = 0;
    g.lastCorrectSides = [];
    g.score = 0;
    g.caught = 0;
    g.missed = 0;
    g.hearts = 3;
    g.alive = true;
    g.hitCooldown = 0;
    g.firstInput = false;
    g.pops = [];
    g.challengeCounter = 0;
    setHud({ score: 0, caught: 0, missed: 0, hearts: 3 });
    promptRef.current = 'Toque pra voar';

    let disposed = false;

    function resize() {
      if (!canvas) return;
      sizeCanvas(canvas, WORLD_W, WORLD_H);
    }
    resize();
    window.addEventListener('resize', resize);

    function flap() {
      if (!g.alive) return;
      g.playerVy = FLAP_V;
      g.firstInput = true;
      sfxRef.current.play('jump');
    }
    function onPointer(e: PointerEvent) {
      e.preventDefault();
      flap();
    }
    function onKey(e: KeyboardEvent) {
      if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW') {
        e.preventDefault();
        flap();
      }
    }
    canvas.addEventListener('pointerdown', onPointer);
    window.addEventListener('keydown', onKey);

    let lastT = performance.now();
    function frame(nowMs: number) {
      if (disposed) return;
      const dt = Math.min(0.05, (nowMs - lastT) / 1000);
      lastT = nowMs;
      update(dt);
      render();
      rafRef.current = requestAnimationFrame(frame);
    }

    function update(dt: number) {
      if (!g.alive) return;

      // Physics (start after first flap — mercy window)
      if (g.firstInput) {
        g.playerVy += GRAVITY * dt;
        g.playerY += g.playerVy * dt;
      }
      // Ceiling
      if (g.playerY < 0) {
        g.playerY = 0;
        g.playerVy = Math.max(g.playerVy, 40);
      }
      // Ground
      if (g.playerY > GROUND_Y - PLAYER_R) {
        g.playerY = GROUND_Y - PLAYER_R;
        if (g.firstInput && g.hitCooldown <= 0) {
          g.playerVy = FLAP_V * 0.7;
          loseHeart();
        } else {
          g.playerVy = 0;
        }
      }

      // Anim
      g.scrollX += SCROLL_SPEED * dt * 0.5;
      g.wingPhase += dt * (8 + Math.max(0, -g.playerVy / 40));
      g.blinkT -= dt;
      if (g.blinkT <= 0) {
        g.blink = 1;
        g.blinkT = 2 + Math.random() * 3;
      } else if (g.blink > 0) {
        g.blink = Math.max(0, g.blink - dt * 8);
      }
      if (g.hitCooldown > 0) g.hitCooldown -= dt;

      // Pop text lifespan
      g.pops = g.pops.filter((p) => p.t > 0);
      for (const p of g.pops) p.t -= dt;

      // Spawn next challenge — only after first flap
      if (g.firstInput) {
        g.spawnTimer -= dt;
        if (g.spawnTimer <= 0) {
          if (g.queue.length === 0) {
            if (g.cards.length === 0) endGame();
          } else {
            // Visual-space guard: se ainda houver card ativo (não
            // consumido, ou consumido mas mostrando feedback) na metade
            // direita da tela, adia o spawn pra não aparecer sobreposto
            // no anterior. Checa ~0.4s depois de novo.
            const blocked = g.cards.some(
              (c) => c.x > SPAWN_GUARD_X && (!c.consumed || c.state !== 'neutral'),
            );
            if (blocked) {
              g.spawnTimer = 0.4;
            } else {
              spawnChallenge(g.queue.shift()!);
              g.spawnTimer = CHALLENGE_INTERVAL;
            }
          }
        }

        // Heart pickup — raro e SEMPRE no intervalo livre entre desafios.
        // Regra: o coração leva ~2.3s da borda direita até o jogador. Se
        // um card ativo vai chegar no player dentro de ±1s dessa janela,
        // ou se o próximo desafio spawna em <1s (encavala o heart que
        // está subindo), adiamos o spawn em 0.5s e tentamos de novo. Isso
        // garante que o coração NUNCA aparece em paralelo com as opções
        // de tradução — o jogador sempre tem tempo de coletar ou ignorar
        // sem conflito.
        g.heartTimer -= dt;
        if (g.heartTimer <= 0) {
          if (g.hearts < MAX_HEARTS) {
            const HEART_ARRIVAL =
              (WORLD_W + HEART_R - PLAYER_X) / SCROLL_SPEED;
            const SAFETY_GAP = 1.0;
            const cardConflict = g.cards.some((c) => {
              if (c.consumed) return false;
              const cardArrival = (c.x - PLAYER_X) / SCROLL_SPEED;
              return Math.abs(cardArrival - HEART_ARRIVAL) < SAFETY_GAP;
            });
            const nextSpawnSoon = g.spawnTimer < SAFETY_GAP;
            if (!cardConflict && !nextSpawnSoon) {
              spawnHeartPickup();
              g.heartTimer =
                HEART_COOLDOWN_MIN +
                Math.random() * (HEART_COOLDOWN_MAX - HEART_COOLDOWN_MIN);
            } else {
              // Zona ocupada — tenta de novo em 0.5s sem resetar o
              // cooldown longo (senão nunca sai do "quase-spawna").
              g.heartTimer = 0.5;
            }
          } else {
            // Vida cheia — cooldown normal, não força spawn.
            g.heartTimer =
              HEART_COOLDOWN_MIN +
              Math.random() * (HEART_COOLDOWN_MAX - HEART_COOLDOWN_MIN);
          }
        }
      }

      // Move cards + handle collision/pass
      for (const c of g.cards) {
        c.x -= SCROLL_SPEED * dt;
        if (c.consumed) continue;

        // Collision: AABB card vs circle player (simplified to AABB)
        const cLeft = c.x - CARD_W / 2;
        const cRight = c.x + CARD_W / 2;
        const cTop = c.y - CARD_H / 2;
        const cBot = c.y + CARD_H / 2;
        const pLeft = PLAYER_X - PLAYER_R + 3;
        const pRight = PLAYER_X + PLAYER_R - 3;
        const pTop = g.playerY - PLAYER_R + 3;
        const pBot = g.playerY + PLAYER_R - 3;
        if (pRight > cLeft && pLeft < cRight && pBot > cTop && pTop < cBot) {
          onCardTouched(c);
          continue;
        }

        // Missed: card scrolled past o jogador sem ser tocado.
        // Antes: o desafio era descartado silenciosamente. Agora conta como
        // erro — quem não escolhe nenhuma opção errou na prática. Perde 1 ♥
        // e o card "correto" acende em vermelho pra o user ver a resposta.
        if (cRight < PLAYER_X - PLAYER_R - 2) {
          // Só resolve uma vez por desafio: usa o card atual como âncora e
          // marca o par inteiro como consumido. Isso evita double-count caso
          // ambos os cards ultrapassem no mesmo frame.
          const siblings = g.cards.filter((s) => s.challengeId === c.challengeId);
          const alreadyResolved = siblings.some(
            (s) => s.consumed && s.state !== 'neutral',
          );
          if (!alreadyResolved) {
            g.missed += 1;
            g.hearts -= 1;
            g.hitCooldown = HIT_GRACE;
            sfxRef.current.play('wrong');
            sfxRef.current.play('heart-loss');
            // Revela o card correto em vermelho com legenda "ignorou"
            for (const sib of siblings) {
              sib.consumed = true;
              sib.state = sib.isCorrect ? 'wrong' : 'neutral';
            }
            const correct = siblings.find((s) => s.isCorrect);
            if (correct) {
              g.pops.push({
                x: correct.x,
                y: correct.y,
                t: 0.9,
                color: 'rgb(238, 85, 85)',
                text: 'ignorou',
              });
            }
            if (g.hearts <= 0) endGame();
            // Em vez de forçar spawn imediato (antes: triggerNextChallenge),
            // só garantimos um delay mínimo de respiro antes do próximo
            // par. O CHALLENGE_INTERVAL natural já dá folga; isso é um
            // piso de segurança.
            g.spawnTimer = Math.max(g.spawnTimer, POST_CHALLENGE_MIN_DELAY);
          } else {
            c.consumed = true;
          }
        }
      }
      g.cards = g.cards.filter((c) => c.x + CARD_W / 2 > -20);

      // Heart pickups — scroll + collision com o player.
      for (const h of g.hearts3d) {
        h.x -= SCROLL_SPEED * dt;
        h.phase += dt * 3;
        if (h.consumed) continue;
        const dx = PLAYER_X - h.x;
        const dy = g.playerY - h.y;
        if (dx * dx + dy * dy < (PLAYER_R + HEART_R) * (PLAYER_R + HEART_R)) {
          h.consumed = true;
          if (g.hearts < MAX_HEARTS) {
            g.hearts += 1;
            sfxRef.current.play('xp-gain');
            g.pops.push({
              x: h.x,
              y: h.y,
              t: 0.9,
              color: 'rgb(238, 85, 85)',
              text: '+1 ♥',
            });
          }
        }
      }
      g.hearts3d = g.hearts3d.filter((h) => h.x + HEART_R > -20);

      // Publish HUD
      setHud((prev) => {
        const score = Math.floor(g.score);
        if (
          prev.score === score &&
          prev.caught === g.caught &&
          prev.missed === g.missed &&
          prev.hearts === g.hearts
        ) {
          return prev;
        }
        return { score, caught: g.caught, missed: g.missed, hearts: g.hearts };
      });
    }

    function spawnChallenge(pair: FlappyPair) {
      g.challengeCounter += 1;
      const id = `c${g.challengeCounter}`;
      // Center the pair vertically with a random offset
      const minCenter = 150 + CARD_PAIR_GAP / 2;
      const maxCenter = GROUND_Y - 90 - CARD_PAIR_GAP / 2;
      const center = minCenter + Math.random() * (maxCenter - minCenter);
      // Randomize which side has the correct answer, but forbid 3 in a row
      // on the same side so the player can't lock onto a pattern.
      let correctOnTop = Math.random() < 0.5;
      const last = g.lastCorrectSides;
      if (last.length >= 2 && last[0] === last[1] && last[0] === correctOnTop) {
        correctOnTop = !correctOnTop;
      }
      g.lastCorrectSides.push(correctOnTop);
      if (g.lastCorrectSides.length > 2) g.lastCorrectSides.shift();
      const topLabel = correctOnTop ? pair.correct : pair.wrong;
      const botLabel = correctOnTop ? pair.wrong : pair.correct;

      // Offset pra direita: card entra um pouco fora da borda pra ganhar
      // tempo de voo. 30px = ~0.2s — suficiente pra separação visual sem
      // arrastar o ritmo.
      const x = WORLD_W + CARD_W / 2 + 30;

      g.cards.push({
        id: id + 'a',
        challengeId: id,
        isCorrect: correctOnTop,
        label: topLabel,
        x,
        y: center - CARD_PAIR_GAP / 2,
        consumed: false,
        state: 'neutral',
      });
      g.cards.push({
        id: id + 'b',
        challengeId: id,
        isCorrect: !correctOnTop,
        label: botLabel,
        x,
        y: center + CARD_PAIR_GAP / 2,
        consumed: false,
        state: 'neutral',
      });

      // Update prompt to the PT target
      const ptWord = pair.promptPt ?? pair.correct;
      promptRef.current = `Toque: "${ptWord}"`;

      // Fala PT-BR. Em produção usa ElevenLabs (modelo eleven_v3, voz
      // configurada no servidor via env ELEVENLABS_VOICE_ID). Se o 503
      // vier (sem API key), o useTts cai em speechSynthesis com picker
      // de voz PT-BR (prefere Luciana/Google/Maria/Francisca/Helena).
      void ttsRef.current.play(ptWord, undefined, 'pt-BR');
    }

    function onCardTouched(card: Card) {
      if (card.consumed) return;
      card.consumed = true;
      card.state = card.isCorrect ? 'correct' : 'wrong';
      // Consume sibling so the round ends cleanly — sibling stays neutral and
      // is hidden (we only reveal the card Slingu actually hit).
      for (const sib of g.cards) {
        if (sib.challengeId === card.challengeId && sib.id !== card.id) {
          sib.consumed = true;
        }
      }

      if (card.isCorrect) {
        g.score += XP_CORRECT;
        g.caught += 1;
        sfxRef.current.play('xp-gain');
        g.pops.push({ x: card.x, y: card.y, t: 0.9, color: 'rgb(88, 204, 2)', text: `+${XP_CORRECT} XP` });
      } else {
        g.missed += 1;
        sfxRef.current.play('heart-loss');
        g.hearts -= 1;
        g.hitCooldown = HIT_GRACE;
        g.pops.push({ x: card.x, y: card.y, t: 0.9, color: 'rgb(238, 85, 85)', text: '✗' });
        if (g.hearts <= 0) endGame();
      }

      // Não forçamos spawn imediato do próximo desafio. O CHALLENGE_INTERVAL
      // regula o ritmo e o POST_CHALLENGE_MIN_DELAY garante que, mesmo se
      // o jogador tocar num card tarde, sempre haja um piso de respiro
      // antes do próximo par aparecer — evita cards "colados".
      g.spawnTimer = Math.max(g.spawnTimer, POST_CHALLENGE_MIN_DELAY);
    }

    function spawnHeartPickup() {
      g.heartCounter += 1;
      // Y dentro da faixa jogável, evitando extremos onde fica fácil demais
      // ou difícil demais de alcançar.
      const minY = 170;
      const maxY = GROUND_Y - 120;
      const y = minY + Math.random() * (maxY - minY);
      g.hearts3d.push({
        id: `h${g.heartCounter}`,
        x: WORLD_W + HEART_R + 10,
        y,
        consumed: false,
        phase: Math.random() * Math.PI * 2,
      });
    }

    function loseHeart() {
      g.missed += 1;
      g.hearts -= 1;
      g.hitCooldown = HIT_GRACE;
      sfxRef.current.play('hit');
      sfxRef.current.play('heart-loss');
      if (g.hearts <= 0) endGame();
    }

    function endGame() {
      if (!g.alive) return;
      g.alive = false;
      sfxRef.current.play('game-over');
      // Persiste recorde local. commitScore só grava se score > anterior.
      const finalScore = Math.floor(g.score);
      const result = commitScore(HIGH_SCORE_ID, finalScore);
      setHighScore(result.best);
      setNewRecord(result.isNew);
      setPhase('over');
    }

    function render() {
      const ctx = canvas!.getContext('2d');
      if (!ctx) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      drawBackground(ctx, WORLD_W, WORLD_H, GROUND_Y, g.scrollX, g.scrollX * 0.5);
      drawGround(ctx, WORLD_W, GROUND_Y, GROUND_H, g.scrollX);

      // Heart pickups (atrás dos cards pra não obstruir texto)
      for (const h of g.hearts3d) {
        if (h.consumed) continue;
        const bob = Math.sin(h.phase) * 3;
        drawHeartPickup(ctx, h.x, h.y + bob);
      }

      // Cards — hide only cards that were consumed WITHOUT feedback (the
      // untouched sibling of a hit, or cards that scrolled past unanswered).
      // Cards Slingu actually touched keep drawing in their result color.
      for (const c of g.cards) {
        if (c.consumed && c.state === 'neutral') continue;
        drawCard(ctx, c.x, c.y, c.label, c.state);
      }

      // Player
      const tilt = Math.max(-0.5, Math.min(0.9, g.playerVy / 700));
      const flashing =
        g.hitCooldown > 0 && Math.floor(g.hitCooldown * 16) % 2 === 0;
      if (!flashing) {
        drawSlingu(ctx, PLAYER_X, g.playerY, {
          radius: PLAYER_R,
          angle: tilt,
          blink: g.blink,
          wingPhase: g.wingPhase,
          wingOpen: 1,
          mouth: g.playerVy < 0 ? 'open' : 'smile',
          groundY: GROUND_Y,
        });
      }

      // Pop text
      for (const p of g.pops) {
        const a = Math.max(0, Math.min(1, p.t / 0.9));
        ctx.save();
        ctx.globalAlpha = a;
        ctx.fillStyle = p.color;
        ctx.font = 'bold 22px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(p.text, p.x, p.y - (1 - a) * 40);
        ctx.restore();
      }

      // Prompt banner — positioned below the DOM HUD pill (which sits at
      // top-left 12px + ~36px tall), so on portrait mobile they don't
      // overlap anymore.
      const promptText = promptRef.current;
      if (promptText) {
        ctx.save();
        const bw = Math.min(WORLD_W - 24, 440);
        const by = 72;
        ctx.fillStyle = 'rgba(255,255,255,0.94)';
        roundRect(ctx, WORLD_W / 2 - bw / 2, by, bw, 46, 23);
        ctx.fill();
        ctx.strokeStyle = 'rgba(20, 83, 163, 0.2)';
        ctx.lineWidth = 1.5;
        roundRect(ctx, WORLD_W / 2 - bw / 2, by, bw, 46, 23);
        ctx.stroke();
        ctx.fillStyle = 'rgb(20, 83, 163)';
        ctx.font = 'bold 20px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(promptText, WORLD_W / 2, by + 23);
        ctx.restore();
      }
    }

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      disposed = true;
      canvas.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', resize);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      startedRef.current = false;
    };
  }, [phase]);

  return (
    <div className="sl-minigame sl-minigame--portrait">
      <button
        type="button"
        className="sl-minigame__close"
        onClick={() => onExitRef.current({ score: 0, caught: 0, missed: 0, xp: 0 })}
        aria-label="Fechar mini-game"
      >
        ×
      </button>

      <canvas ref={canvasRef} className="sl-minigame__canvas" />

      <div className="sl-flappy__hud">
        <span className="sl-quizrun__hearts">
          {'♥'.repeat(hud.hearts)}
          {'·'.repeat(Math.max(0, 3 - hud.hearts))}
        </span>
        <span className="sl-quizrun__score">{hud.score} XP</span>
      </div>
      <div className="sl-flappy__sfx">
        <SfxToggle variant="icon" />
      </div>

      {phase === 'menu' ? (
        <div className="sl-flappy__menu">
          <h2>
            <span aria-hidden>🪽</span> Flappy Slingu
          </h2>
          <p>
            Toque ou espaço pra bater asa. Uma palavra em português aparece
            e duas opções <strong style={{ color: 'rgb(28, 176, 246)' }}>azuis</strong>{' '}
            flutuam — escolha a tradução certa. Se acertar, o card vira{' '}
            <strong style={{ color: 'rgb(88, 204, 2)' }}>verde</strong>; se
            errar, vira <strong style={{ color: 'rgb(238, 85, 85)' }}>vermelho</strong>.
            Você também ouve a palavra certa em inglês. Mais de 1000 palavras
            misturadas, do A1 ao B2.
          </p>
          {highScore > 0 ? (
            <p className="sl-flappy__record">
              🏆 Seu recorde: <strong>{highScore} XP</strong>
            </p>
          ) : null}
          <div className="sl-flappy__menu-actions">
            <button
              type="button"
              className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
              onClick={() => {
                sfx.play('start');
                setNewRecord(false);
                setPhase('playing');
              }}
            >
              Começar
            </button>
          </div>
        </div>
      ) : null}

      {phase === 'over' ? (
        <div className="sl-flappy__over">
          <h2>
            {newRecord
              ? '🏆 Novo recorde!'
              : hud.caught > hud.missed
                ? 'Mandou bem!'
                : 'Quase lá!'}
          </h2>
          <p>
            XP: <strong>{hud.score}</strong> · Acertos: {hud.caught} · Erros:{' '}
            {hud.missed}
          </p>
          <p className="sl-flappy__record">
            🏆 Recorde: <strong>{highScore} XP</strong>
            {newRecord ? ' ✨' : null}
          </p>
          <div className="sl-flappy__menu-actions">
            <button
              type="button"
              className="sl-btn sl-btn--ghost"
              onClick={() =>
                onExitRef.current({
                  score: hud.score,
                  caught: hud.caught,
                  missed: hud.missed,
                  xp: hud.score,
                })
              }
            >
              Voltar pro app
            </button>
            <button
              type="button"
              className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
              onClick={() => {
                sfx.play('start');
                setHud({ score: 0, caught: 0, missed: 0, hearts: 3 });
                setPhase('playing');
              }}
            >
              Jogar de novo
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function drawCard(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  text: string,
  state: CardState,
) {
  const bg =
    state === 'correct'
      ? 'rgb(88, 204, 2)'
      : state === 'wrong'
        ? 'rgb(238, 85, 85)'
        : 'rgb(28, 176, 246)';
  const shade =
    state === 'correct'
      ? 'rgb(58, 136, 18)'
      : state === 'wrong'
        ? 'rgb(180, 40, 40)'
        : 'rgb(20, 124, 186)';
  const w = CARD_W;
  const h = CARD_H;
  ctx.save();
  // drop shadow
  ctx.globalAlpha = 0.25;
  ctx.fillStyle = shade;
  roundRect(ctx, cx - w / 2 + 2, cy - h / 2 + 5, w, h, 18);
  ctx.fill();
  ctx.globalAlpha = 1;
  // body
  ctx.fillStyle = bg;
  roundRect(ctx, cx - w / 2, cy - h / 2, w, h, 18);
  ctx.fill();
  // inner highlight
  ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
  roundRect(ctx, cx - w / 2 + 4, cy - h / 2 + 4, w - 8, 14, 14);
  ctx.fill();
  // outline
  ctx.strokeStyle = shade;
  ctx.lineWidth = 3;
  roundRect(ctx, cx - w / 2, cy - h / 2, w, h, 18);
  ctx.stroke();
  // text
  ctx.fillStyle = 'rgb(255, 255, 255)';
  ctx.font = 'bold 26px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, cx, cy + 2);
  ctx.restore();
}

/**
 * Coração flutuante (pickup de vida). Simples, com glow rosa pra destacar
 * do fundo azul e não competir com os cards de palavra.
 */
function drawHeartPickup(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  ctx.save();
  // Glow externo
  ctx.shadowColor = 'rgba(238, 85, 85, 0.55)';
  ctx.shadowBlur = 16;
  const r = 18;
  const w = r * 1.6;
  const h = r * 1.45;
  const x = cx - w / 2;
  const y = cy - h / 2;
  ctx.beginPath();
  ctx.moveTo(cx, y + h);
  ctx.bezierCurveTo(cx - w * 0.7, y + h * 0.55, cx - w * 0.55, y - h * 0.1, cx, y + h * 0.3);
  ctx.bezierCurveTo(cx + w * 0.55, y - h * 0.1, cx + w * 0.7, y + h * 0.55, cx, y + h);
  ctx.closePath();
  const grad = ctx.createLinearGradient(cx, y, cx, y + h);
  grad.addColorStop(0, 'rgb(255, 120, 140)');
  grad.addColorStop(1, 'rgb(220, 55, 75)');
  ctx.fillStyle = grad;
  ctx.fill();
  // Contorno branco fino pra contraste
  ctx.shadowBlur = 0;
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.stroke();
  // Reflexo no canto superior esquerdo
  ctx.beginPath();
  ctx.ellipse(cx - w * 0.22, y + h * 0.28, w * 0.13, h * 0.08, -0.5, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.65)';
  ctx.fill();
  ctx.restore();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.min(r, Math.abs(w) / 2, Math.abs(h) / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}
